import React, { useContext, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import ColorPicker from "../../../../../../Elements/ColorPicker";
import { NavigatorContext } from "../../../../../../../utils/navigatorContext";
import { GoogleFontPanel, googleFontValue } from "../../../editorToolbox/toolBox/GoogleFontSelect";
import { familyFromValue, loadGoogleFonts, loadGoogleFontsInHtml } from "../../../../../../../utils/googleFontLoader";

const EDITOR_ID = "tiny-editor";
const FONT_PANEL_WIDTH = 260;
const FONT_PANEL_HEIGHT = 320;

const selectionFontFamily = (editor) => {
  const node = editor.dom.getParent(editor.selection.getNode(), (n) => !!n.style?.fontFamily, editor.getBody());
  return familyFromValue(node?.style.fontFamily);
};

function TextEditorSection({ handleTextChange, html_text, errorState, totalPages, currentPage, height = 220 }) {
  const { brandColors, addBrandColor, removeBrandColor } = useContext(NavigatorContext);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerAnchor, setPickerAnchor] = useState(null);
  const [pickerColor, setPickerColor] = useState("#000000");
  const savedRangeRef = useRef(null);
  const collapsedRef = useRef(false);
  const pendingColorRef = useRef(null);
  const applyOnMouseupRef = useRef(false);
  const pickerOpenRef = useRef(false);
  const changeHandlerRef = useRef(handleTextChange);
  const [fontPanel, setFontPanel] = useState(null);
  const wrapRef = useRef(null);
  const fontPanelRef = useRef(null);
  const fontButtonRef = useRef(null);
  const fontRangeRef = useRef(null);
  pickerOpenRef.current = pickerOpen;

  const openFontPanel = (editor, button) => {
    const wrap = wrapRef.current;
    if (!wrap || !button) return;
    fontButtonRef.current = button;
    fontRangeRef.current = editor.selection.getRng().cloneRange();
    const rect = button.getBoundingClientRect();
    const fitsBelow = window.innerHeight - rect.bottom >= FONT_PANEL_HEIGHT + 8 || rect.top < FONT_PANEL_HEIGHT + 8;
    const next = {
      top: fitsBelow ? rect.bottom + 4 : rect.top - FONT_PANEL_HEIGHT - 4,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - FONT_PANEL_WIDTH - 8)),
      family: selectionFontFamily(editor),
      container: wrap.closest(".modal") || document.body,
    };
    setFontPanel((open) => (open ? null : next));
  };

  const applyFont = (font) => {
    setFontPanel(null);
    const editor = window.tinymce?.get(EDITOR_ID);
    if (!editor) return;
    editor.focus();
    if (fontRangeRef.current) editor.selection.setRng(fontRangeRef.current);
    if (font) {
      loadGoogleFonts([font.family], editor.getDoc());
      editor.execCommand("FontName", false, googleFontValue(font));
    } else {
      editor.formatter.remove("fontname", { value: null }, null, true);
      editor.undoManager.add();
    }
    editor.nodeChanged();
    changeHandlerRef.current?.(editor.getContent());
  };

  useEffect(() => {
    if (!fontPanel) return undefined;
    const handleOutside = (e) => {
      if (fontButtonRef.current?.contains(e.target)) return;
      if (fontPanelRef.current && !fontPanelRef.current.contains(e.target)) setFontPanel(null);
    };
    const handleScroll = (e) => {
      if (fontPanelRef.current?.contains(e.target)) return;
      setFontPanel(null);
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [fontPanel]);

  const formatColor = (editor, value) => {
    if (value) editor.formatter.apply("forecolor", { value });
    else editor.formatter.remove("forecolor", { value: null }, null, true);
  };

  const applyPendingColor = (editor, atSavedRange) => {
    const pending = pendingColorRef.current;
    pendingColorRef.current = null;
    if (!pending) return;
    editor.focus();
    if (atSavedRange && savedRangeRef.current) editor.selection.setRng(savedRangeRef.current);
    formatColor(editor, pending.value);
  };

  const applyTextColor = (value) => {
    setPickerColor(value);
    if (collapsedRef.current) {
      pendingColorRef.current = { value };
      return;
    }
    const editor = window.tinymce?.get(EDITOR_ID);
    if (!editor) return;
    if (savedRangeRef.current) editor.selection.setRng(savedRangeRef.current);
    editor.undoManager.ignore(() => formatColor(editor, value));
    savedRangeRef.current = editor.selection.getRng().cloneRange();
    changeHandlerRef.current?.(editor.getContent());
  };

  const handlePickerClose = () => {
    const editor = window.tinymce?.get(EDITOR_ID);
    if (!editor) return;
    if (!applyOnMouseupRef.current) applyPendingColor(editor, true);
    editor.undoManager.add();
    changeHandlerRef.current?.(editor.getContent());
  };

  useEffect(() => {
    changeHandlerRef.current = handleTextChange;
  }, [handleTextChange]);

  useEffect(() => {
    const initialiseEditor = () => {
      if (window.tinymce?.get(EDITOR_ID)) {
        window.tinymce.get(EDITOR_ID).remove();
      }

      // Capture current values for use in TinyMCE setup
      const currentTotalPages = totalPages;
      const currentPageValue = currentPage;

      window.tinymce.init({
        selector: `#${EDITOR_ID}`,
        height,
        menubar: false,
        toolbar_mode: "wrap",
        statusbar: false,
        paste_as_text: true,
        font_size_formats:
          "8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt 60pt 72pt 96pt",
        plugins: [
          "autolink",
          "link",
          "lists",
        ],
        extended_valid_elements: "span[data-page-link|style|class]",
        block_formats: "Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3",
        toolbar:
        "undo redo | blocks kzfontfamily fontsizeinput | kztextcolor | bold italic underline removeformat | alignleft aligncenter alignright alignjustify | numlist bullist | link jumptopage",
        content_style: "body { line-height: 1.4; margin: 0; padding-left: 10px; padding-right: 0; } p { margin: 1em 0; } h1 { font-size: 1.75em; font-weight: 700; margin: 0.67em 0; } h2 { font-size: 1.5em; font-weight: 600; margin: 0.75em 0; } h3 { font-size: 1.25em; font-weight: 600; margin: 0.83em 0; } span[data-page-link] { cursor: pointer; text-decoration: underline; color: #0066cc; } .mce-content-body:not([dir=rtl])[data-mce-placeholder]:not(.mce-visualblocks)::before { left: 10px !important; } .mce-content-body[dir=rtl][data-mce-placeholder]:not(.mce-visualblocks)::before { right: 10px !important; } .mce-content-body[dir=rtl] body { padding-left: 0; padding-right: 10px; }",
        link_default_protocol: "https",
        link_assume_external_targets: true,
        link_confirm_external: false,
        link_target_list: [
            { title: "New tab", value: "_blank" }
        ],
        link_attributes_postprocess: (attrs) => {
           if (
               attrs.href &&
               !attrs.href.startsWith("http") &&
               !attrs.href.startsWith("/") &&
               !attrs.href.startsWith("#") &&
               !attrs.href.startsWith("mailto:") &&
               !attrs.href.startsWith("tel:")
           ) {
               attrs.href = `https://${attrs.href}`;
           }
           attrs.target = "_blank";
           attrs.rel = "noopener noreferrer";
           return attrs;
        },
        setup(editor) {
          editor.ui.registry.addButton('kzfontfamily', {
            text: 'Default font',
            tooltip: 'Font family',
            onSetup: (api) => {
              const update = () => api.setText(selectionFontFamily(editor) || 'Default font');
              editor.on('NodeChange', update);
              return () => editor.off('NodeChange', update);
            },
            onAction: () => {
              const active = document.activeElement;
              const button = active?.getAttribute?.('aria-label') === 'Font family'
                ? active
                : editor.getContainer().querySelector('button[aria-label="Font family"]');
              openFontPanel(editor, button);
            }
          });

          editor.on("SetContent", () => {
            loadGoogleFontsInHtml(editor.getContent(), editor.getDoc());
          });

          editor.on("mousedown", () => setFontPanel(null));

          editor.ui.registry.addButton('kztextcolor', {
            icon: 'text-color',
            tooltip: 'Text color',
            onAction: () => {
              const active = document.activeElement;
              const button = active?.getAttribute?.('aria-label') === 'Text color'
                ? active
                : editor.getContainer().querySelector('button[aria-label="Text color"]');
              editor.undoManager.add();
              savedRangeRef.current = editor.selection.getRng().cloneRange();
              collapsedRef.current = savedRangeRef.current.collapsed;
              pendingColorRef.current = null;
              setPickerColor(editor.getWin().getComputedStyle(editor.selection.getNode()).color || "#000000");
              setPickerAnchor(button);
              setPickerOpen((open) => !open);
            }
          });

          editor.on("mousedown", () => {
            if (!pickerOpenRef.current) return;
            if (pendingColorRef.current) {
              applyOnMouseupRef.current = true;
              editor.once("mouseup", () => {
                applyOnMouseupRef.current = false;
                applyPendingColor(editor, false);
              });
            }
            setPickerOpen(false);
          });

          editor.ui.registry.addButton('jumptopage', {
            icon: 'arrow-right',
            tooltip: 'Jump to Page',
            onAction: function() {
              const selectedText = editor.selection.getContent({ format: 'text' });
              if (!selectedText.trim()) {
                editor.windowManager.alert('Please select some text first.');
                return;
              }

              editor.windowManager.open({
                title: 'Jump to Page',
                body: {
                  type: 'panel',
                  items: [
                    {
                      type: 'input',
                      name: 'pageNumber',
                      label: `Page number (1-${currentTotalPages || '?'}):`,
                      inputMode: 'numeric'
                    }
                  ]
                },
                buttons: [
                  {
                    type: 'cancel',
                    text: 'Cancel'
                  },
                  {
                    type: 'submit',
                    text: 'Apply',
                    primary: true
                  }
                ],
                onSubmit: function(api) {
                  const data = api.getData();
                  const pageNumber = parseInt(data.pageNumber);
                  
                  // Validation
                  if (isNaN(pageNumber) || pageNumber < 1) {
                    editor.windowManager.alert('Please enter a valid page number (1 or greater).');
                    return;
                  }
                  
                  if (currentTotalPages && pageNumber > currentTotalPages) {
                    editor.windowManager.alert(`Page number cannot exceed ${currentTotalPages}.`);
                    return;
                  }
                  
                  if (pageNumber === currentPageValue) {
                    editor.windowManager.alert('Please enter a page number different from the current page.');
                    return;
                  }

                  // Get the selected content as HTML
                  const selectedContent = editor.selection.getContent();
                  
                  // Wrap selected content in a span with data-page-link attribute
                  // Insert span followed by a space to ensure cursor has valid position
                  const spanHtml = `<span data-page-link="${pageNumber}" style="cursor: pointer; text-decoration: underline; color: #0066cc;">${selectedContent || selectedText}</span> `;
                  
                  // Insert the span with trailing space
                  editor.selection.setContent(spanHtml);
                  
                  // Move cursor outside the link span (after the space) - Gmail-like UX
                  setTimeout(() => {
                    // Find the inserted span via current selection
                    const range = editor.selection.getRng();
                    const node = range.startContainer;
                    let insertedSpan = null;
                    
                    if (node.nodeType === 3) {
                      insertedSpan = node.parentElement?.closest('span[data-page-link]');
                    } else if (node.nodeType === 1) {
                      insertedSpan = node.closest ? node.closest('span[data-page-link]') : null;
                    }
                    
                    // Fallback: find the last span with data-page-link
                    if (!insertedSpan) {
                      const allSpans = editor.getBody().querySelectorAll('span[data-page-link]');
                      if (allSpans.length > 0) {
                        insertedSpan = allSpans[allSpans.length - 1];
                      }
                    }
                    
                    // Position cursor after the span (in the trailing space)
                    if (insertedSpan && insertedSpan.nextSibling && insertedSpan.nextSibling.nodeType === 3) {
                      const spaceNode = insertedSpan.nextSibling;
                      const newRange = editor.dom.createRng();
                      newRange.setStart(spaceNode, 1); // Position at end of space
                      newRange.setEnd(spaceNode, 1);
                      editor.selection.setRng(newRange);
                      editor.selection.collapse(false);
                    } else if (insertedSpan) {
                      // Fallback: position after span
                      const newRange = editor.dom.createRng();
                      newRange.setStartAfter(insertedSpan);
                      newRange.setEndAfter(insertedSpan);
                      editor.selection.setRng(newRange);
                      editor.selection.collapse(false);
                    }
                    
                    // Clear all active formatting to ensure plain text
                    try {
                      editor.formatter.remove('link');
                      editor.formatter.remove('bold');
                      editor.formatter.remove('italic');
                      editor.formatter.remove('underline');
                    } catch (err) {
                      // Ignore formatter errors
                    }
                    
                    editor.selection.normalize();
                    editor.focus();
                    changeHandlerRef.current?.(editor.getContent());
                  }, 0);
                  
                  api.close();
                }
              });
            }
          });

          editor.on("init", () => {
            editor.setContent(html_text || "");
            
            // Inject custom style for placeholder position and content padding after editor initialization
            const styleId = 'tinymce-placeholder-custom-style';
            if (!document.getElementById(styleId)) {
              const style = document.createElement('style');
              style.id = styleId;
              style.textContent = `
                .mce-content-body:not([dir=rtl])[data-mce-placeholder]:not(.mce-visualblocks)::before {
                  left: 10px !important;
                }
                .mce-content-body[dir=rtl][data-mce-placeholder]:not(.mce-visualblocks)::before {
                  right: 10px !important;
                }
                .mce-content-body:not([dir=rtl]) body {
                  padding-left: 10px !important;
                  padding-right: 0 !important;
                }
                .mce-content-body[dir=rtl] body {
                  padding-left: 0 !important;
                  padding-right: 10px !important;
                }
                .tox-statusbar__path {
                  display: none !important;
                }
                .tox .tox-tbtn[aria-label="Font family"] {
                  width: 130px;
                  justify-content: flex-start;
                }
                .tox .tox-tbtn[aria-label="Font family"] .tox-tbtn__select-label {
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                }
              `;
              document.head.appendChild(style);
            }
          });

          const updateContent = () => {
            changeHandlerRef.current?.(editor.getContent());
          };

          // Handle space key to exit link context (Gmail-like behavior)
          // This is a fallback in case cursor somehow ends up inside a link
          editor.on("keydown", (e) => {
            if (e.keyCode === 32 || e.key === ' ') {
              const range = editor.selection.getRng();
              const node = range.startContainer;
              
              // Check if cursor is inside a link span
              let linkSpan = null;
              if (node.nodeType === 3) {
                linkSpan = node.parentElement?.closest('span[data-page-link]');
                // Only exit if at the end of the link text
                if (linkSpan && range.startOffset < node.textContent.length) {
                  return; // Allow normal space insertion inside link
                }
              } else if (node.nodeType === 1) {
                linkSpan = node.closest ? node.closest('span[data-page-link]') : null;
              }
              
              // If inside a link span at the end, exit link context
              if (linkSpan && linkSpan.parentNode) {
                e.preventDefault();
                e.stopPropagation();
                
                const newRange = editor.dom.createRng();
                newRange.setStartAfter(linkSpan);
                newRange.setEndAfter(linkSpan);
                editor.selection.setRng(newRange);
                editor.selection.collapse(false);
                
                // Clear formatting
                try {
                  editor.formatter.remove('link');
                  editor.formatter.remove('bold');
                  editor.formatter.remove('italic');
                  editor.formatter.remove('underline');
                } catch (err) {
                  // Ignore formatter errors
                }
                
                editor.insertContent(' ');
                editor.selection.normalize();
                editor.focus();
                updateContent();
              }
            }
          });

          editor.on("keyup", updateContent);
          editor.on("change", updateContent);
        },
        placeholder: "Start writing...",
      });
    };

    if (window.tinymce) {
      initialiseEditor();
    } else {
      const script = document.createElement("script");
      script.src = "/tinymce/tinymce.min.js";
      script.addEventListener("load", initialiseEditor);
      document.body.appendChild(script);

      return () => {
        script.removeEventListener("load", initialiseEditor);
      };
    }

    return () => {
      if (window.tinymce) {
        const existing = window.tinymce.get(EDITOR_ID);
        if (existing) {
          existing.remove();
        }
      }
    };
  }, [totalPages, currentPage]);

  useEffect(() => {
    if (window.tinymce) {
      const editor = window.tinymce.get(EDITOR_ID);
      // Check if editor and selection are fully initialized before accessing
      if (editor && editor.selection && editor.getContent() !== (html_text || "")) {
        // Save cursor position before updating content
        const bookmark = editor.selection.getBookmark(2, true);

        // Update content
        editor.setContent(html_text || "");

        // Restore cursor position
        editor.selection.moveToBookmark(bookmark);
      }
    }
  }, [html_text]);

  return (
    <>
      <div ref={wrapRef}>
        <textarea
          id={EDITOR_ID}
          defaultValue={html_text}
          style={{ width: "100%", height: "150px" }}
        />
        {fontPanel && ReactDOM.createPortal(
          <div ref={fontPanelRef}>
            <GoogleFontPanel
              className="googleFontSelect__panel--toolbar"
              style={{ top: fontPanel.top, left: fontPanel.left }}
              selectedFamily={fontPanel.family}
              onSelect={applyFont}
            />
          </div>,
          fontPanel.container
        )}
        {errorState?.textErr && <p className="text-danger">{errorState?.textErr}</p>}
        {pickerAnchor && (
          <ColorPicker
            anchorEl={pickerAnchor}
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            onClose={handlePickerClose}
            color={pickerColor}
            onChange={applyTextColor}
            alpha={true}
            brandColors={brandColors}
            onAddBrandColor={addBrandColor}
            onRemoveBrandColor={removeBrandColor}
          />
        )}
      </div>
    </>
  );
}

export default TextEditorSection;
